import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

@Component({
  selector: 'app-metro-upload-images',
  templateUrl: './metro-upload-images.component.html',
  standalone: false
})
export class MetroUploadImagesComponent{

  @Input() multiple: boolean = false;
  @Input() accept: string = 'image/*';
  @Input() maxFileSize: number = 5 * 1024 * 1024; // 5MB default
  @Input() placeholderText: string = '::DropImagesHereOrClickToBrowse';
  @Input() buttonText: string = '::UploadImage';
  @Input() set initialImageUrl(url: string | null) {
    if (url) {
      this.images = [{
        file: null,
        url,
        name: url
      }];
    }
  }


  // Ref al input propio de ESTA instancia. Antes se usaba document.getElementById('fileInput'),
  // que con dos uploaders en la misma pantalla (imagen principal + galería) siempre devolvía
  // el primero → los archivos de la galería terminaban en la imagen de perfil (winepassport#344).
  @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>;

  @Output() imageSelected = new EventEmitter<File[]>();
  @Output() imageRemoved = new EventEmitter<number>();
  @Output() validationError = new EventEmitter<string>();

  private static readonly VALID_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];

  images: Array<{ file: File; url: string; name: string }> = [];
  isDragOver = false;
  errorMessage: string | null = null;


  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    if (event.dataTransfer?.files) {
      this.handleFiles(event.dataTransfer.files);
    }
  }

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
  }

  handleFiles(files: FileList): void {
    this.errorMessage = null;
    const newFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file && this.isValidFile(file)) {
        if (!this.multiple && (this.images.length > 0 || newFiles.length > 0)) {
          this.images = [];
          newFiles.length = 0;
        }
        newFiles.push(file);
      }
    }

    if (newFiles.length > 0) {
      this.addFiles(newFiles);
    }
  }

  isValidFile(file: File): boolean {
    // En mobile (cámara o algunas apps de galería, iOS y Android) el navegador a veces no
    // informa el MIME type (file.type llega vacío) para una foto real. En ese caso, no
    // rechazamos de una: validamos por extensión del nombre de archivo como fallback.
    const hasValidMimeType = file.type.startsWith('image/');
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const hasValidExtension = MetroUploadImagesComponent.VALID_IMAGE_EXTENSIONS.includes(extension);

    if (!hasValidMimeType && !(file.type === '' && hasValidExtension)) {
      this.errorMessage = 'Only image files are allowed';
      this.validationError.emit(this.errorMessage);
      return false;
    }

    if (file.size > this.maxFileSize) {
      this.errorMessage = `File size exceeds ${this.maxFileSize / (1024 * 1024)}MB limit`;
      this.validationError.emit(this.errorMessage);
      return false;
    }

    return true;
  }

  addFiles(files: File[]): void {
    const newImages = files.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));

    if (this.multiple) {
      this.images = [...this.images, ...newImages];
    } else {
      this.images = newImages;
    }

    // Emit the original files
    this.imageSelected.emit(files);
  }

  removeImage(index: number): void {
    const removedImage = this.images[index];
    URL.revokeObjectURL(removedImage.url); // Free memory
    this.images.splice(index, 1);
    this.imageRemoved.emit(index);
  }

  triggerFileInput(): void {
    this.fileInputRef?.nativeElement.click();
  }

  onImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/images/no-image.png';
  }
}
